// Maps FeedbackController's error codes to the copy key FeedbackCompose
// should show. Pulled out of the component (and unit-tested) because this is
// exactly the kind of mapping that silently rots: the server gains a new
// FeedbackSubmissionRules code, nobody touches the client, and a player sees
// a bare uppercase code instead of a sentence. A test that enumerates every
// code FeedbackController.java can currently return catches that on day one
// instead of via a support ticket.
import type { MessageKey } from '../../../shared/i18n/messages';

const FEEDBACK_ERROR_COPY: Record<string, MessageKey> = {
  // FeedbackSubmissionRules — content and image validation, mirrored
  // client-side for immediate feedback but re-checked (and the source of
  // truth) on the server.
  FEEDBACK_CONTENT_TOO_SHORT: 'feedbackErrTooShort',
  FEEDBACK_CONTENT_TOO_LONG: 'feedbackErrTooLong',
  // The client only ever offers 3 image slots, so this code is defensive
  // (e.g. a future client bug) rather than reachable through this screen —
  // there's no dedicated "too many images" string, so it shares the
  // too-large copy rather than showing nothing.
  FEEDBACK_TOO_MANY_IMAGES: 'feedbackErrImageTooLarge',
  FEEDBACK_IMAGE_TOO_LARGE: 'feedbackErrImageTooLarge',
  FEEDBACK_IMAGE_TYPE_UNSUPPORTED: 'feedbackErrImageType',
  // FeedbackController#submit — a file the server received but could not
  // even read back off disk. Closest available copy is the type/format one;
  // this is not a "too large" problem.
  FEEDBACK_IMAGE_UNREADABLE: 'feedbackErrImageType',
  // Object storage rejected the write — nothing the player did wrong, and no
  // specific copy for it, so it falls back to the generic submit failure.
  FEEDBACK_STORAGE_WRITE_FAILED: 'feedbackErrSubmit',
  // FeedbackSubmissionRules#validateRate
  FEEDBACK_RATE_LIMITED: 'feedbackErrRateLimited',
  FEEDBACK_TOO_FREQUENT: 'feedbackErrTooFrequent',
  // The token expired or was revoked between opening this screen and
  // submitting — same prompt the compose entry point shows up front.
  UNAUTHORIZED: 'feedbackLoginRequired',
};

/**
 * @param code The ApiError's `.message` (i.e. the server's `code` field), or
 *   undefined/null for a non-ApiError failure (network error, JSON parse
 *   failure, etc).
 */
export function feedbackErrorCopyKey(code: string | null | undefined): MessageKey {
  if (!code) return 'feedbackErrSubmit';
  return FEEDBACK_ERROR_COPY[code] ?? 'feedbackErrSubmit';
}
